<script setup lang="ts">
import { Wrench } from '@lucide/vue'
import { type MustGateEntry, metricList, type ToolSlot, toolEntryNames } from '@zerohuman/shared'
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    Textarea,
    ZhConfirmButton,
    ZhFieldError,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusPill,
    ZhSystemLabel,
    ZhTooltip,
} from '@zerohuman/ui'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
    api,
    CRAFTED_NOT_IN_PLAN_MESSAGE,
    isCraftedRefusal,
    modelTierLabel,
    type Role,
    type RunRow,
    requestFailure,
    runStoppedLine,
    type SavedCatalogPublication,
    type TaskCopyInput,
    type TaskDetail,
    type TaskGateSummary,
    type TaskInputField,
    type TaskRecommendation,
    type TaskRow,
    type TaskRunStatus,
    type TaskSuccessor,
    type TaskVersionDetail,
} from '@/api'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import DefinitionIssueLine from '@/components/DefinitionIssueLine.vue'
import DocsLink from '@/components/DocsLink.vue'
import ModelTierField from '@/components/ModelTierField.vue'
import TaskCatalogControl from '@/components/TaskCatalogControl.vue'
import TaskCopyForm from '@/components/TaskCopyForm.vue'
import TaskFlowLane from '@/components/TaskFlowLane.vue'
import TaskGatesEditor, { type TaskGate } from '@/components/TaskGatesEditor.vue'
import TaskInputsEditor from '@/components/TaskInputsEditor.vue'
import TaskInputValues from '@/components/TaskInputValues.vue'
import TaskKpiReview from '@/components/TaskKpiReview.vue'
import TaskRoleAssignment from '@/components/TaskRoleAssignment.vue'
import TaskSuccessorsEditor from '@/components/TaskSuccessorsEditor.vue'
import TaskToolSlotsEditor from '@/components/TaskToolSlotsEditor.vue'
import TaskToolsPicker from '@/components/TaskToolsPicker.vue'
import TaskTriggers from '@/components/TaskTriggers.vue'
import { focusById, useFieldValidation } from '@/composables/useFieldValidation'
import { useFormDraft } from '@/composables/useFormDraft'
import { useSuccessorChoices } from '@/composables/useSuccessorChoices'
import { useUiMode } from '@/composables/useUiMode'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import { optionalAmount, requiredName, SLUG_TAKEN } from '@/lib/fieldRules'
import { fileAsDataUrl } from '@/lib/fileDataUrl'
import { buildGateBadges } from '@/lib/gateBadges'
import { typedInputType } from '@/lib/inputFields'
import { useLlmModels } from '@/lib/llmModels'
import { pickedId } from '@/lib/memberPickers'
import { grantWrites, type NavGrant } from '@/lib/navGrant'
import { cloneOnLabel, ownerVersionLabel } from '@/lib/ownerVersion'
import { toolActionLabel, toolLabel } from '@/lib/presentation'
import { requiredGateRow } from '@/lib/requiredGates'
import { heldSourceUpdateLine } from '@/lib/sourceUpdateHeld'
import { runPillStatus } from '@/lib/statusTone'
import {
    type ConflictChoice,
    type ConflictField,
    cloneValues,
    conflictFields,
    hasOwnEdits,
    mergeReady,
    mergeValues,
    shownValue,
    type TaskEditorValues,
} from '@/lib/taskConflict'
import { assigneeRoleIssue, workspaceJsonIssue } from '@/lib/taskFieldRules'
import { assignableRoles } from '@/lib/taskRoles'
import { groupToolsByVendor } from '@/lib/toolChips'
import { optionalToolsText, toolEditorText, toolEntriesFromEditor } from '@/lib/toolEntries'
import { versionOriginLabel } from '@/lib/versionOrigin'
import { type TaskKpi, taskKpisApi } from '@/task-kpis-api'

const route = useRoute()
const router = useRouter()
/**
 * #1749: the task's kind (llm / deterministic) shows in the header and the edit form in Advanced only, and a tool
 * outside the edit form reads by `shownTool` ("GitHub: merge pull request" in Standard, the raw name in Advanced).
 */
const { mode: uiMode } = useUiMode()
const advancedMode = computed(() => uiMode.value === 'advanced')
/**
 * A tool as the page prints it, in the current mode. Standard gives the vendor and the action in words, so two gates
 * on one vendor's tools never read alike; Advanced gives the raw name. Grouping, keys and saving keep the raw name.
 */
function shownTool(toolName: string): string {
    return toolActionLabel(toolName, uiMode.value)
}
const task = ref<TaskDetail | null>(null)
const roles = ref<Role[]>([])
const recommendations = ref<TaskRecommendation[]>([])
/** The current version's KPIs (#900); null when they could not be read, so that is said rather than shown as none. */
const taskKpis = ref<TaskKpi[] | null>([])
const runs = ref<RunRow[]>([])
const runTotal = ref(0)
const runPage = ref(1)
const runPages = ref(1)
/** Run history and the read-only definition follow this version; defaults to the current one. */
const selectedVersionId = ref<string | null>(null)
const versionDetail = ref<TaskVersionDetail | null>(null)
const versions = computed(() => task.value?.versions ?? [])
const selectedVersion = computed(() => versions.value.find(row => row.id === selectedVersionId.value) ?? null)
const viewingOlderVersion = computed(
    () => Boolean(selectedVersionId.value) && selectedVersionId.value !== task.value?.currentVersionId,
)
const isOs = computed(() => task.value?.origin === 'os')
/** A deprecated task is not new work: Start is disabled like Copy, and the API would refuse it (`task_not_runnable`). */
const deprecated = computed(() => task.value?.status === 'deprecated')
/** Every run of a tenant task goes to whoever holds its role on its team; an OS task runs as the OS. */
const runsAsLabel = computed(() => {
    if (isOs.value) {
        return 'OS'
    }
    const role = task.value?.assigneeRoleName ?? task.value?.assigneeRole
    return role ? `whoever holds ${role}` : 'nobody. This task needs a role before it can run.'
})
const canEditDefinition = computed(() => !isOs.value && task.value?.canEditDefinition === true)
/**
 * Whether the task may start new runs (#1316), as the server last said: the fetched task, then the saved value a
 * switch answered with. Never the last run's outcome or the definition's lifecycle. A task never switched reads `active`.
 */
const runStatus = computed<TaskRunStatus>(() => task.value?.runStatus ?? 'active')
/**
 * The viewer's grant from `api.me()`, the one place the portal reads it: `undefined` until read, null when it could
 * not be read, which restricts nothing (`grantWrites`) because the server refuses a write the grant does not cover.
 */
const viewerGrant = ref<NavGrant | null | undefined>(undefined)
/** Switching needs `tasks:write`, as the route does. A built-in task offers no switch at all (see the template). */
const canChangeRunStatus = computed(() => viewerGrant.value !== undefined && grantWrites(viewerGrant.value, 'tasks'))
const canAssignRole = computed(() => viewerGrant.value !== undefined && grantWrites(viewerGrant.value, 'roles'))
/**
 * The tasks with a run-status save in flight, by id. The page is reused across `/tasks/:id`, so a save still pending
 * for a task the page has left must not hold the switch on the task now shown.
 */
const runStatusSavesInFlight = ref(new Set<string>())
/** A run-status save in flight for the task shown: its switch is held until the server answers, so a double click is one write. */
const savingRunStatus = computed(() => Boolean(task.value && runStatusSavesInFlight.value.has(task.value.id)))
/** One line per metric on the current version, so the template lists them without re-parsing on every read. */
const currentMetrics = computed(() => metricList(task.value?.currentVersion?.metric?.text))
/** Same, for whichever older version is selected for read-only viewing. */
const versionMetrics = computed(() => metricList(versionDetail.value?.metric?.text))
const editing = ref(false)
/** The tasks and outcomes the successors editor picks from (#1754), read when an edit starts. */
const successorChoices = useSuccessorChoices()
const { tasks: successorTasks, outcomes: successorOutcomes } = successorChoices
const copying = ref(false)
const copyBusy = ref(false)
const error = ref<string | null>(null)
/** The page-level message is the plan's refusal of a Zero Human update (#449), so it links to Billing. */
const planRefused = computed(() => error.value === CRAFTED_NOT_IN_PLAN_MESSAGE)
const saving = ref(false)
const deleting = ref(false)
const starting = ref(false)
const retrying = ref<string | null>(null)
/** Pulling the source's newer version into this clone (#414): only ever started by the Update button. */
const updatingFromSource = ref(false)
const operationBusy = computed(
    () => saving.value || deleting.value || starting.value || retrying.value !== null || updatingFromSource.value,
)
/** The source's newer version this linked clone could pull, with what it adds. Null when there is none to offer. */
const sourceUpdate = computed(() => (isOs.value ? null : (task.value?.sourceUpdate ?? null)))
/**
 * The owner's version this linked clone is on (#1260), shown as the headline version in place of the clone's own
 * counter, which stays in the version history. Null for a task that is not a linked clone.
 */
const ownerVersion = computed(() => task.value?.ownerVersion ?? null)
/** The owner's version an unlinked copy was taken from. Null for anything else. */
const copiedFromVersion = computed(() => task.value?.copiedFromVersion ?? null)
/**
 * A gate as `tool · owner (approval)`, then `→ outcome` when it is tagged with one. The API tells gates apart by
 * outcome too, so a gate whose only change is its outcome reads differently as removed and as added.
 */
const gateLabel = (gate: TaskGateSummary): string =>
    `${shownTool(gate.tool)} · ${gate.owner} (${gate.approval})${gate.outcome ? ` → ${gate.outcome}` : ''}`
/** The gates the newer version adds. */
const sourceUpdateGates = computed(() => (sourceUpdate.value?.addedGates ?? []).map(gateLabel))
/** The tools the clone has that the newer version drops: the update removes them. */
const sourceUpdateRemovedTools = computed(() => sourceUpdate.value?.removedTools ?? [])
/** The gates the clone has that the newer version drops: the update removes them. */
const sourceUpdateRemovedGates = computed(() => (sourceUpdate.value?.removedGates ?? []).map(gateLabel))
/** The inputs the newer version requires that nothing this enterprise sets fills (F2): set them as input values. */
const sourceUpdateMissingInputs = computed(() => sourceUpdate.value?.missingInputs ?? [])
/** True when the update neither adds nor drops a declared tool or gate, and leaves no input to fill. */
const sourceUpdateUnchanged = computed(
    () =>
        !sourceUpdate.value?.addedTools.length &&
        !sourceUpdateGates.value.length &&
        !sourceUpdateRemovedTools.value.length &&
        !sourceUpdateRemovedGates.value.length &&
        !sourceUpdateMissingInputs.value.length,
)
/** Why the server says the update is waiting rather than applied by itself (#763); null when it is not held. */
const sourceUpdateHeld = computed(() => sourceUpdate.value?.held ?? null)
/** The one line naming every reason it is held, tools labelled as the panel's chips are; empty when not held. */
const sourceUpdateHeldLine = computed(() => {
    const update = sourceUpdate.value
    return update?.held ? heldSourceUpdateLine(update, update.held, shownTool) : ''
})
const taskBusy = computed(() => operationBusy.value || copyBusy.value)
const retryable = new Set(['failed', 'cancelled', 'skipped_overlap', 'blocked'])

const form = ref({
    name: '',
    slug: '',
    kind: 'llm',
    status: 'active',
    assigneeRole: '',
    goal: '',
    metric: '',
    guardrails: '',
    tools: '',
    /** The tools marked optional, one per line: a run goes ahead without one that is not connected. */
    optionalTools: '',
    spendUsd: '',
    /** Empty runs the default model. A directory id pins this task to that model. */
    model: '',
    workspace: '',
    /** One outcome per line. A run that ends on one, with nothing following it, finishes the work. */
    finishesOn: '',
    /**
     * Where a mailbox offers this task as a one-click button: on the mailbox's list, on one open mail, or nowhere
     * (empty). A button on one mail starts the run with that mail's id as its `emailId` input.
     */
    mailboxActionOn: '' as '' | 'mailbox' | 'mail',
    /** What the button says, such as "Clean inbox". */
    mailboxActionLabel: '',
    skillMd: '',
})
/** A task runs as the company's role of that name, whatever team it sits on: the select offers every role. */
const teamRoles = computed(() => assignableRoles(roles.value))

/** What the role picker holds. No role chosen is held as no value at all, so it reads its placeholder. */
const roleChoice = computed(() => form.value.assigneeRole || undefined)

/**
 * The teams that inherit the task: each team where a member holds its role, seated or as lead. Read-only: a task
 * belongs to a role, not a team, so a team gets its work by seating a holder of the role.
 */
const taskTeamIds = computed(() => (task.value?.teams ?? []).map(team => team.id))
const taskTeamsLabel = computed(() => {
    const names = (task.value?.teams ?? []).map(team => team.name)
    return names.length ? names.join(', ') : 'No team yet: nobody holds this role on a team'
})

function chooseRole(choice: unknown): void {
    form.value.assigneeRole = pickedId(choice)
}

/**
 * The rules a task save already has, checked while typing (#1757): a name; a role whenever there is one to choose (the
 * Role select's own `required`); a spend per run that is blank or 0 or more; Workspace JSON that parses. Slug has no
 * rule of its own: only the server can say it is taken, and that refusal is shown on it.
 */
const fields = useFieldValidation(
    computed(() => ({
        name: form.value.name,
        slug: form.value.slug,
        assigneeRole: form.value.assigneeRole,
        spendUsd: form.value.spendUsd,
        workspace: form.value.workspace,
    })),
    {
        name: requiredName('task'),
        slug: () => null,
        assigneeRole: value => assigneeRoleIssue(value, teamRoles.value.length),
        spendUsd: optionalAmount('Spend per run'),
        workspace: workspaceJsonIssue,
    },
    { idPrefix: 'task-edit' },
)
/** Structured editors for inputs/gates/successors; save() sends these, the JSON toggle only edits them. */
const inputRows = ref<TaskInputField[]>([])
const gateRows = ref<TaskGate[]>([])
function requireToolGates(entries: MustGateEntry[]): void {
    for (const entry of entries) {
        const row = requiredGateRow(gateRows.value, entry)
        if (row) gateRows.value.push(row)
    }
}
const successorRows = ref<TaskSuccessor[]>([])
/** The tool slots the version declares; saved as null when there are none. */
const toolSlotRows = ref<ToolSlot[]>([])
/**
 * The version the form was filled from. Save sends every field, so a form from a replaced version would publish that
 * old definition over the new one; the API refuses it (`task_version_conflict`), and a stored draft of one is dropped.
 */
const baseVersionId = ref<string | null>(null)
type TaskEditor = TaskEditorValues<typeof form.value>
/**
 * The values the form was filled from, beside `baseVersionId` (#1757). A conflict compares them with the newer
 * version, to tell the fields this person changed from the ones the other version changed.
 */
const baseValues = ref<TaskEditor | null>(null)
// Stored only while editing: a snapshot written by merely opening the page outlived the version it was taken from.
const draft = useFormDraft(
    `task-edit:${String(route.params.id)}`,
    { form, inputRows, gateRows, successorRows, toolSlotRows, baseVersionId, baseValues },
    { enabled: () => editing.value },
)
// A restored draft is not a change made on this page: its fields show no error until changed or left.
fields.reset()

/** A save refused because the task moved on: the newer task, what it changed, and the person's merge choices. */
interface TaskConflict {
    latest: TaskDetail
    theirs: TaskEditor
    changed: ConflictField[]
    /** Merge was pressed while a field changed on both sides: the panel asks for a side per field. */
    picking: boolean
    choices: Record<string, ConflictChoice>
    /** Reload was pressed with edits in the form: the panel asks before it drops them. */
    confirmingReload: boolean
}
/** The panel's id: a conflict moves focus to it, since Save sits at the foot of a long form and the panel at its top. */
const CONFLICT_PANEL_ID = 'task-conflict-panel'
const conflict = ref<TaskConflict | null>(null)
/** The newer version as the panel names it: its number, when it was saved and, when the API says, by whom. */
const conflictVersion = computed(() => {
    const latest = conflict.value?.latest
    if (!latest) return null
    const row = latest.versions.find(version => version.id === latest.currentVersionId)
    const by = (row as { createdByName?: string | null } | undefined)?.createdByName
    return {
        number: row?.version ?? latest.currentVersion?.version ?? null,
        savedAt: row?.createdAt ? new Date(row.createdAt).toLocaleString() : null,
        by: by || null,
    }
})
/** True until the first load when the form came back from storage rather than from the server. */
let restoredDraft = form.value.slug !== ''
const notice = ref<string | null>(null)

/** `v-model` on `type="number"` inputs stores numbers, so values are not always strings. */
const runInputs = reactive<Record<string, string | number | boolean>>({})
/** The file picked for each `file` input of the Start form (os#679), sent as a `data:` URL when the run starts. */
const runFiles = reactive<Record<string, File | null>>({})

/** What a typed Start field shows: the kit `Input` holds text or a number, never the boolean a checkbox stores. */
function startText(key: string): string | number {
    const value = runInputs[key]
    return typeof value === 'boolean' ? '' : (value ?? '')
}

function pickFile(key: string, event: Event): void {
    const target = event.target
    runFiles[key] = target instanceof HTMLInputElement ? (target.files?.[0] ?? null) : null
}

const recommendationColumns: DataTableColumn[] = [
    { key: 'title', label: 'Title', type: 'text', filterable: false },
    { key: 'summary', label: 'Summary', type: 'text', filterable: false, class: 'max-w-sm truncate' },
    {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
            { value: 'pending', label: 'pending' },
            { value: 'implementing', label: 'implementing' },
            { value: 'complete', label: 'complete' },
            { value: 'error', label: 'error' },
            { value: 'declined', label: 'declined' },
            { value: 'actioned', label: 'actioned' },
        ],
    },
    { key: 'confidence', label: 'Confidence', type: 'number', filterable: false },
    { key: 'createdAt', label: 'Created', type: 'date', filterable: false },
]

const runColumns: DataTableColumn[] = [
    {
        key: 'status',
        label: 'Status',
        sort: 'status',
        type: 'select',
        options: [
            { value: 'queued', label: 'queued' },
            { value: 'running', label: 'running' },
            { value: 'waiting_on_gate', label: 'waiting_on_gate' },
            { value: 'succeeded', label: 'succeeded' },
            { value: 'failed', label: 'failed' },
            { value: 'cancelled', label: 'cancelled' },
            { value: 'skipped_overlap', label: 'skipped_overlap' },
            { value: 'paused_spend', label: 'paused_spend' },
        ],
    },
    { key: 'id', label: 'Run', type: 'text', filterable: false },
    { key: 'createdAt', label: 'Started', sort: 'createdAt', type: 'date', filterable: false },
    { key: 'finishedAt', label: 'Finished', type: 'date', filterable: false },
]

const inputFields = computed(() => task.value?.currentVersion?.inputs ?? [])

const predecessorLinks = computed(() => task.value?.predecessors ?? [])
const successorLinks = computed(() => task.value?.successorTasks ?? [])

/** One chip per vendor (the part before the first dot), keeping its methods for the chip's title. */
const toolChips = computed(() => groupToolsByVendor(task.value?.currentVersion?.tools))

/** The tool slots the current version declares, each a label and the key its tools are named by. */
const toolSlots = computed(() => task.value?.currentVersion?.toolSlots ?? [])
/** The one-click button this task puts on a mailbox, as its current version declares it, or null. */
const mailboxAction = computed(() => task.value?.currentVersion?.mailboxAction ?? null)
const MAILBOX_SURFACE_TEXT: Record<'mailbox' | 'mail', string> = { mailbox: 'on the mailbox', mail: 'on one mail' }

/** Gates read as `tool · owner` badges instead of the JSON the editor stores. */
const gateBadges = computed(() => buildGateBadges(task.value?.currentVersion?.gates))

/**
 * #1755: a hand-picked model's chip reads by mode, as the Model field does: its name in Standard (`Unlisted model` when
 * the connected providers' list lacks it), the exact ID in Advanced. The list is read only for a task that has one.
 */
const { loadLlmModels, modelName } = useLlmModels()
let llmModelsRequested = false
watch(
    () => task.value?.currentVersion?.spend?.model,
    model => {
        if (model && !llmModelsRequested) {
            llmModelsRequested = true
            void loadLlmModels()
        }
    },
)

/** The spend cap as plain chips: money per run, then the model tier it may reach for. */
const spendChips = computed(() => {
    const spend = task.value?.currentVersion?.spend
    const chips: string[] = []
    if (spend?.moneyUsdPerRun !== undefined && spend.moneyUsdPerRun !== null) {
        chips.push(`$${Number(spend.moneyUsdPerRun).toFixed(2)} / run`)
    }
    if (spend?.model) {
        chips.push(modelName(spend.model, uiMode.value))
    } else if (spend?.maxModelTier) {
        chips.push(`${modelTierLabel(spend.maxModelTier)} tier`)
    }
    return chips
})

onMounted(async () => {
    void loadViewerGrant()
    await load()
})

/** Reads the viewer's grant beside the task: a slow or failed read never holds the page up, it only gates the switch. */
async function loadViewerGrant(): Promise<void> {
    try {
        viewerGrant.value = await api.me()
    } catch {
        viewerGrant.value = null
    }
}

watch(
    () => route.params.id,
    async () => {
        editing.value = false
        copying.value = false
        copyBusy.value = false
        form.value.slug = ''
        await load()
    },
)

/**
 * The current version's KPIs (#900), read beside the page rather than in front of it: a slow or failed KPI read
 * never holds up or breaks the rest of the task, and a read for a task the page has since left is dropped.
 */
async function loadKpis(taskId: string): Promise<void> {
    const kpis = await taskKpisApi.forTask(taskId).catch(() => null)
    if (task.value?.id === taskId) {
        taskKpis.value = kpis
    }
}

/** A KPI signed off or withdrawn: its list as the server answered, and the task again, which may have a new version. */
async function kpisChanged(kpis: TaskKpi[]): Promise<void> {
    taskKpis.value = kpis
    await load()
}

async function load(): Promise<void> {
    try {
        const [detail, roleRows, recRows] = await Promise.all([
            api.task(String(route.params.id)),
            api.roles(),
            api.taskRecommendations(String(route.params.id)),
        ])
        task.value = detail
        roles.value = roleRows
        recommendations.value = recRows.filter(row => row.status !== 'complete')
        void loadKpis(detail.id)
        if (!detail.versions.some(row => row.id === selectedVersionId.value)) {
            // `?version=<id>` (e.g. from a run page) opens that version; otherwise the current one.
            const requested = typeof route.query.version === 'string' ? route.query.version : ''
            const fallback = detail.currentVersionId ?? detail.versions[0]?.id ?? null
            selectedVersionId.value = detail.versions.some(row => row.id === requested) ? requested : fallback
            versionDetail.value = null
            runPage.value = 1
        }
        await loadRuns(runPage.value)
        if (selectedVersionId.value && selectedVersionId.value !== detail.currentVersionId && !versionDetail.value) {
            versionDetail.value = await api.taskVersion(detail.id, selectedVersionId.value)
        }
        const stale = !editing.value && baseVersionId.value !== detail.currentVersionId
        if (!form.value.slug || stale) {
            if (stale && restoredDraft && baseVersionId.value) {
                notice.value = 'An unsaved edit of an older version of this task was discarded.'
            }
            fillForm(detail)
            draft.clear()
        }
        restoredDraft = false
        for (const field of detail.currentVersion?.inputs ?? []) {
            if (runInputs[field.key] === undefined) {
                runInputs[field.key] = startValue(field, ownInputValue(detail, field.key))
            }
        }
        error.value = null
    } catch (caught) {
        // Not found only when the API says so (os#1687): every other failure, a render fault in shaping the task
        // included, read as "Task not found." and sent the first diagnosis after a role and a team that were fine.
        error.value =
            requestFailure(caught)?.status === 404
                ? 'Task not found.'
                : 'Could not load this task. Reload the page to try again.'
    }
}

/** This enterprise's own value for an input (F2), when the current version takes it; else undefined. */
function ownInputValue(detail: TaskDetail, key: string): string | number | boolean | undefined {
    return detail.ignoredInputValues?.includes(key) ? undefined : detail.inputValues?.[key]
}

/**
 * What a Start form field holds before anyone types: a checkbox's boolean, else the enterprise's own value (F2), else
 * the version's default, else nothing. The same order the start itself fills a field it is not given.
 */
function startValue(field: TaskInputField, own?: string | number | boolean): string | boolean {
    const value = own ?? field.default
    if (field.type === 'boolean') {
        return value === true
    }
    return value === undefined || value === null ? '' : String(value)
}

function fillForm(detail: TaskDetail): void {
    const values = editorValuesOf(detail)
    applyEditorValues(values)
    baseValues.value = cloneValues(values)
    baseVersionId.value = detail.currentVersionId ?? null
}

/** The form filled with `values`, as if it had just opened on them: no field shows an error yet. */
function applyEditorValues(values: TaskEditor): void {
    const copy = cloneValues(values)
    form.value = copy.form
    inputRows.value = copy.inputs as TaskInputField[]
    gateRows.value = copy.gates as TaskGate[]
    successorRows.value = copy.successors as TaskSuccessor[]
    toolSlotRows.value = (copy.toolSlots ?? []) as ToolSlot[]
    fields.reset()
}

/** What the form holds now, detached from it. */
function currentEditorValues(): TaskEditor {
    return cloneValues({
        form: form.value,
        inputs: inputRows.value,
        gates: gateRows.value,
        successors: successorRows.value,
        toolSlots: toolSlotRows.value,
    })
}

function editorValuesOf(detail: TaskDetail): TaskEditor {
    const version = detail.currentVersion
    const values = {
        name: detail.name ?? detail.slug,
        slug: detail.slug,
        kind: detail.kind,
        status: detail.status ?? 'active',
        assigneeRole: detail.assigneeRole ?? '',
        goal: version?.goal ?? '',
        metric: version?.metric?.text ?? '',
        guardrails: (version?.guardrails ?? []).join('\n'),
        tools: toolEditorText(version?.tools),
        optionalTools: optionalToolsText(version?.tools),
        spendUsd: version?.spend?.moneyUsdPerRun === undefined ? '' : String(version.spend.moneyUsdPerRun),
        model: version?.spend?.model ?? '',
        workspace: JSON.stringify(version?.workspace ?? null, null, 2),
        finishesOn: (version?.finishesOn ?? []).join('\n'),
        mailboxActionOn: version?.mailboxAction?.on ?? ('' as const),
        mailboxActionLabel: version?.mailboxAction?.label ?? '',
        skillMd: detail.skillMd,
    }
    return {
        form: values,
        inputs: version?.inputs ? [...version.inputs] : [],
        gates: version?.gates ? [...version.gates] : [],
        successors: version?.successors ? [...version.successors] : [],
        toolSlots: version?.toolSlots ? [...version.toolSlots] : [],
    }
}

/**
 * The save was refused because the task moved on (#1757): read the task as it is now and list, by label, what the
 * newer version changed from the values this form was filled from. The person's edits stay in the form.
 */
async function openConflict(taskId: string): Promise<void> {
    let latest: TaskDetail
    try {
        latest = await api.task(taskId)
    } catch {
        error.value =
            'This task has a newer version than the one you were editing, and it could not be read. Try again.'
        return
    }
    const theirs = editorValuesOf(latest)
    conflict.value = {
        latest,
        theirs,
        changed: conflictFields(baseValues.value, currentEditorValues(), theirs),
        picking: false,
        choices: {},
        confirmingReload: false,
    }
    // Focus brings the panel into view and has a screen reader read it: pressing Save must visibly do something.
    await nextTick()
    focusById(CONFLICT_PANEL_ID)
}

/** The form now builds on the newer version: Save publishes against it. Nothing is published here. */
function rebaseOnto(latest: TaskDetail, theirs: TaskEditor, values: TaskEditor): void {
    task.value = latest
    applyEditorValues(values)
    baseValues.value = cloneValues(theirs)
    baseVersionId.value = latest.currentVersionId ?? null
    conflict.value = null
}

/**
 * Reload: take the newer version whole, and stay editing on it. This person's edits are dropped, so with any in the
 * form the panel first asks; one click never throws them away.
 */
function reloadConflict(): void {
    const open = conflict.value
    if (!open) return
    if (!open.confirmingReload && hasOwnEdits(baseValues.value ?? open.theirs, currentEditorValues())) {
        open.confirmingReload = true
        return
    }
    rebaseOnto(open.latest, open.theirs, open.theirs)
}

/** "Keep my edits" on the Reload question: back to the choice between Merge and Reload, nothing dropped. */
function keepEditsOnReload(): void {
    if (conflict.value) conflict.value.confirmingReload = false
}

/**
 * Merge: keep this person's changes on top of the newer version. Where both sides changed a field the panel first
 * asks for a side, per field, with no default.
 *
 * The form stays editable under the panel, so what changed is worked out again from what the form holds now, not
 * from what it held when Save was refused: an edit made since is kept, or asked about, never silently replaced.
 */
function mergeConflict(): void {
    const open = conflict.value
    if (!open) return
    const mine = currentEditorValues()
    open.changed = conflictFields(baseValues.value, mine, open.theirs)
    if (!mergeReady(open.changed, open.choices)) {
        open.picking = true
        return
    }
    rebaseOnto(open.latest, open.theirs, mergeValues(mine, open.changed, open.choices))
}

/** A side picked for a field both sides changed; the merge completes once every such field has one. */
function chooseConflictSide(key: string, side: ConflictChoice): void {
    const open = conflict.value
    if (!open) return
    open.choices = { ...open.choices, [key]: side }
    mergeConflict()
}

/**
 * Cancel throws the edit away (left in the form, it became the starting point of the next one) and reloads, so an
 * edit refused because the task moved on starts again from the version that replaced it.
 */
async function toggleEdit(): Promise<void> {
    copying.value = false
    conflict.value = null
    if (!editing.value) {
        notice.value = null
        editing.value = true
        fields.reset()
        void successorChoices.load()
        return
    }
    editing.value = false
    draft.clear()
    form.value.slug = ''
    await load()
}

/**
 * A published task's catalog entry takes every version saved after, unless the catalog refuses the version: the save
 * still stands, and this says which version the catalog still offers and the catalog's own reason.
 */
function catalogKeptNotice(publication: SavedCatalogPublication | null | undefined): string | null {
    if (!publication?.refusal) return null
    const kept = publication.sourceVersion === null ? 'its earlier version' : `v${publication.sourceVersion}`
    return `Saved. The catalog still offers ${kept}: ${publication.refusal.message}`
}

function parseJson<T>(raw: string, fallback: T): T {
    if (!raw.trim()) {
        return fallback
    }
    return JSON.parse(raw) as T
}

async function save(): Promise<void> {
    if (!task.value || !canEditDefinition.value || saving.value || copyBusy.value) {
        return
    }
    if (fields.check() !== null) {
        return
    }
    const taskId = task.value.id
    saving.value = true
    error.value = null
    conflict.value = null
    try {
        const draftForm = form.value
        const saved = await api.saveTaskVersion(task.value.id, {
            name: draftForm.name,
            slug: draftForm.slug,
            kind: draftForm.kind,
            status: draftForm.status,
            assigneeRole: draftForm.assigneeRole || null,
            goal: draftForm.goal,
            metric: { text: draftForm.metric },
            guardrails: draftForm.guardrails
                .split('\n')
                .map(line => line.trim())
                .filter(Boolean),
            // A draft stored before the field existed has none: every entry keeps the mark it was stored with.
            tools: toolEntriesFromEditor(draftForm.tools, task.value.currentVersion?.tools, draftForm.optionalTools),
            inputs: inputRows.value,
            gates: gateRows.value,
            spend: {
                moneyUsdPerRun: draftForm.spendUsd === '' ? undefined : Number(draftForm.spendUsd),
                // The form has no field for the tier, and a published spend replaces the version's whole: left out,
                // a save would drop it. It is carried as it was, beside a picked model too, where it waits unused.
                maxModelTier: task.value.currentVersion?.spend?.maxModelTier,
                model: draftForm.model || undefined,
            },
            workspace: parseJson(draftForm.workspace, null),
            successors: successorRows.value,
            // A draft stored before this field existed has none: read it as nothing declared.
            finishesOn: (draftForm.finishesOn ?? '')
                .split('\n')
                .map(line => line.trim())
                .filter(Boolean),
            // None declared is sent as null, which clears any the version had.
            toolSlots: toolSlotRows.value.length ? toolSlotRows.value : null,
            // A surface with a label is a button; anything less is none, sent as null to clear one the version had.
            mailboxAction:
                draftForm.mailboxActionOn && (draftForm.mailboxActionLabel ?? '').trim()
                    ? { on: draftForm.mailboxActionOn, label: (draftForm.mailboxActionLabel ?? '').trim() }
                    : null,
            skillMd: draftForm.skillMd,
            baseVersionId: baseVersionId.value,
        })
        draft.clear()
        editing.value = false
        await load()
        notice.value = catalogKeptNotice(saved?.catalogPublication)
    } catch (caught) {
        const code = caught instanceof Error ? caught.message : ''
        if (code === 'task_version_conflict') {
            await openConflict(taskId)
        } else if (code === 'task_slug_taken') {
            // A slug only the server can say is taken: shown on Slug with its fix, as Create task does (#1757).
            fields.refuse('slug', SLUG_TAKEN)
            // The form's fieldset is disabled while saving, and a disabled control takes no focus.
            saving.value = false
            await nextTick()
            focusById(fields.fieldId('slug'))
        } else {
            // Any other refusal maps to no field (#1757): a plain message, the code in Advanced only.
            const plain = 'This task could not be saved. Your edits are still here: try again.'
            error.value = advancedMode.value && code ? `${plain} (${code})` : plain
        }
    } finally {
        saving.value = false
    }
}

function createCopy(body: TaskCopyInput): Promise<TaskRow> {
    if (!task.value) return Promise.reject(new Error('task_not_found'))
    return api.copyTask(task.value.id, body)
}

function copied(created: TaskRow): void {
    copying.value = false
    void router.push(`/tasks/${created.id}`)
}

/**
 * Moves this clone onto its source's newer version. The clonee asked: nothing else calls this. A run already in flight
 * stays on the version it started; the page then reads the task again, on its new version.
 */
async function updateFromSource(): Promise<void> {
    if (!task.value || !sourceUpdate.value || taskBusy.value) {
        return
    }
    updatingFromSource.value = true
    error.value = null
    try {
        task.value = await api.updateTaskFromSource(task.value.id, task.value.currentVersionId ?? null)
        await load()
    } catch (caught) {
        // A Zero Human source the plan does not include (#449): the plan's sentence, beside a link to Billing.
        if (isCraftedRefusal(caught)) error.value = CRAFTED_NOT_IN_PLAN_MESSAGE
        else error.value = caught instanceof Error ? `Could not update (${caught.message}).` : 'Could not update.'
    } finally {
        updatingFromSource.value = false
    }
}

/**
 * Saves the switch's new position (#1316) through the run-status PATCH, with no confirmation: pausing destroys
 * nothing and the same switch undoes it. The page moves only once the server answers, and to what it answered, so a
 * refusal leaves the last saved value showing beside the error. An answer for a task the page has since left, saved or
 * refused, is dropped, and its pending save never holds the switch of the task now shown.
 */
async function changeRunStatus(on: boolean): Promise<void> {
    if (!task.value || savingRunStatus.value) {
        return
    }
    const taskId = task.value.id
    runStatusSavesInFlight.value.add(taskId)
    error.value = null
    try {
        const saved = await api.setTaskRunStatus(taskId, on ? 'active' : 'disabled')
        if (task.value?.id === taskId) {
            task.value.runStatus = saved.runStatus
        }
    } catch (caught) {
        if (task.value?.id === taskId) {
            error.value = `Could not change the run status (${caught instanceof Error ? caught.message : String(caught)}).`
        }
    } finally {
        runStatusSavesInFlight.value.delete(taskId)
    }
}

async function remove(): Promise<void> {
    // ZhConfirmButton has already asked; this guard is only about having a task to delete.
    if (!task.value || taskBusy.value) {
        return
    }
    deleting.value = true
    error.value = null
    try {
        await api.deleteTask(task.value.id)
        draft.clear()
        await router.push('/tasks')
    } catch (caught) {
        const code = caught instanceof Error ? caught.message : ''
        error.value = code === 'task_has_active_runs' ? 'Cancel active runs first.' : 'Could not delete task.'
    } finally {
        deleting.value = false
    }
}

/** What `startInputOf` answers for a required Start field left empty. */
const MISSING = Symbol('missing')

/**
 * What one Start form field sends: a checkbox's boolean, a number, text (a link's URL included, #1754), or a picked
 * file as a `data:` URL (os#679). `undefined` leaves the field out of the body, as for an optional file nobody picked
 * or an optional field left blank, so a blank number is never sent as 0; `MISSING` is a required field left empty.
 */
async function startInputOf(field: TaskInputField): Promise<unknown> {
    // A checkbox always has an answer, so a boolean is never missing.
    if (field.type === 'boolean') {
        return runInputs[field.key] === true
    }
    if (field.type === 'file') {
        const picked = runFiles[field.key]
        if (picked) {
            return fileAsDataUrl(picked)
        }
        return field.required ? MISSING : undefined
    }
    const raw = String(runInputs[field.key] ?? '')
    if (raw.trim() === '') {
        return field.required ? MISSING : undefined
    }
    return field.type === 'number' ? Number(raw) : raw
}

async function start(): Promise<void> {
    if (!task.value || taskBusy.value || deprecated.value) {
        return
    }
    starting.value = true
    error.value = null
    try {
        const body: Record<string, unknown> = {}
        for (const field of inputFields.value) {
            const value = await startInputOf(field)
            if (value === MISSING) {
                error.value = `${field.label} is required.`
                return
            }
            if (value !== undefined) {
                body[field.key] = value
            }
        }
        // #1796: a run is model work, so without an AI account nothing is sent and the prompt opens.
        if (!(await requireAiAccount())) return
        const run = await api.startRun(task.value.id, body)
        await router.push(`/runs/${run.id}`)
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not start (${caught.message}).` : 'Could not start run.'
    } finally {
        starting.value = false
    }
}

async function retryRun(id: string): Promise<void> {
    if (taskBusy.value) return
    // #1796: a retry starts model work, so without an AI account nothing is sent and the prompt opens.
    if (!(await requireAiAccount())) return
    retrying.value = id
    error.value = null
    try {
        const next = await api.retryRun(id)
        await router.push(`/runs/${next.id}`)
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not retry (${caught.message}).` : 'Could not retry.'
    } finally {
        retrying.value = null
    }
}

async function selectVersion(versionId: string): Promise<void> {
    selectedVersionId.value = versionId
    versionDetail.value = null
    await loadRuns(1)
    if (task.value && versionId !== task.value.currentVersionId) {
        try {
            versionDetail.value = await api.taskVersion(task.value.id, versionId)
        } catch {
            error.value = 'Could not load that version.'
        }
    }
}

/** The run history's version picker: a choice of version shows that version's runs, as a click on its row does. */
async function chooseVersion(choice: unknown): Promise<void> {
    if (choice !== null && choice !== undefined && String(choice) !== selectedVersionId.value) {
        await selectVersion(String(choice))
    }
}

async function loadRuns(nextPage: number): Promise<void> {
    runPage.value = nextPage
    const listed = await api.runs({
        taskId: String(route.params.id),
        taskVersionId: selectedVersionId.value ?? undefined,
        page: nextPage,
        limit: 10,
        sort: 'createdAt',
    })
    runs.value = listed.data
    runTotal.value = listed.total
    runPages.value = listed.pages
}
</script>

<template>
	<section class="w-full space-y-6 p-8">
		<p class="text-muted-foreground text-sm">
			<RouterLink class="underline" to="/tasks">Tasks</RouterLink>
		</p>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<RouterLink v-if="planRefused" to="/billing#plans">See plans on Billing</RouterLink>
		<p v-if="notice" class="text-muted-foreground text-sm" data-testid="task-draft-notice">{{ notice }}</p>
		<template v-if="task">
			<div class="flex flex-wrap items-start justify-between gap-3">
				<div>
					<ZhPageTitle kicker="Task">{{ task.name || task.slug }}</ZhPageTitle>
					<p class="text-muted-foreground text-sm">
						{{ task.slug }}<template v-if="!ownerVersion"> · v{{ task.currentVersion?.version ?? 1 }}</template><template v-if="advancedMode"> · {{ task.kind }}</template>
						<ZhSystemLabel v-if="isOs" as="span" tone="system" class="ml-2">OS task</ZhSystemLabel>
					</p>
					<p v-if="ownerVersion" data-testid="owner-version" class="text-sm">
						{{ ownerVersionLabel(ownerVersion.ownerName, task.name || task.slug, ownerVersion.version) }}<span v-if="ownerVersion.availableVersion != null" class="text-muted-foreground"> · {{ cloneOnLabel(ownerVersion.version, ownerVersion.availableVersion) }}</span>
					</p>
					<p v-else-if="copiedFromVersion != null" data-testid="copied-from-version" class="text-muted-foreground text-sm">
						Your own copy, copied from version {{ copiedFromVersion }} of the catalog task. It keeps its own versions and never follows the original.
					</p>
				</div>
				<div v-if="!isOs" class="flex flex-wrap gap-2">
					<Button type="button" size="sm" variant="outline" :disabled="!canEditDefinition || taskBusy" @click="toggleEdit">{{ editing ? 'Cancel edit' : 'Edit' }}</Button>
					<Button type="button" size="sm" variant="outline" :disabled="taskBusy || task.status === 'deprecated' || !task.currentVersion" @click="copying = !copying; editing = false">Copy</Button>
					<ZhConfirmButton
						label="Delete"
						confirm-label="Delete task"
						:title="`Delete ${task.name}?`"
						description="The task definition and every version of it go. Runs that already happened are kept."
						:disabled="taskBusy"
						@confirm="remove"
					/>
				</div>
			</div>

			<!-- The off switch (#1316): whether this task may start new runs. Its own field, never the last run's outcome
			     or the definition's lifecycle. A built-in task's one row serves every enterprise, so it has no switch. -->
			<div class="flex flex-wrap items-center gap-3 text-sm" data-testid="task-run-status-row">
				<span id="task-run-status-label" class="text-muted-foreground">Run status</span>
				<Switch
					v-if="!isOs"
					data-testid="task-run-status-toggle"
					aria-labelledby="task-run-status-label"
					:model-value="runStatus === 'active'"
					:disabled="!canChangeRunStatus || savingRunStatus"
					@update:model-value="changeRunStatus"
				/>
				<ZhStatusPill data-testid="task-run-status" :tone="runStatus === 'active' ? 'running' : 'neutral'" :pulse="false">{{ runStatus }}</ZhStatusPill>
				<span v-if="runStatus === 'disabled'" class="text-muted-foreground">No new runs start until it is switched back on.</span>
			</div>

			<!-- Whether the task is in the catalog, for a task this company owns: a linked clone is another company's. -->
			<TaskCatalogControl v-if="!isOs && canEditDefinition" :task="task" :disabled="taskBusy" @changed="load" />

			<ZhPanel v-if="!isOs && !canEditDefinition" as="p" data-testid="definition-read-only" class="text-sm">This definition belongs to another enterprise. Its details are read-only and self-improvement is disabled here. You can assign a local role below. Copy this task to create an independent definition you can edit.</ZhPanel>
			<!-- Each tool and gate the update adds or drops is a kit chip: a gate waits on someone, so it takes the
			     action pill; a dropped one takes the failed pill and is struck through. -->
			<ZhPanel v-if="sourceUpdate && !editing" data-testid="source-update" class="space-y-2 text-sm">
				<p class="font-medium">Source v{{ sourceUpdate.sourceVersion }} is available</p>
				<p class="text-muted-foreground">This clone stays on its current version until you update it. Runs already in flight finish on the version they started.</p>
				<p v-if="sourceUpdate.addedTools.length" data-testid="source-update-tools">
					<span class="text-muted-foreground">Adds tools:</span>
					<ZhSystemLabel v-for="tool in sourceUpdate.addedTools" :key="tool" as="span" class="ml-1">{{ shownTool(tool) }}</ZhSystemLabel>
				</p>
				<p v-if="sourceUpdateGates.length" data-testid="source-update-gates">
					<span class="text-muted-foreground">Adds gates:</span>
					<ZhStatusPill v-for="gate in sourceUpdateGates" :key="gate" tone="action" :pulse="false" class="ml-1"><span class="font-mono">{{ gate }}</span></ZhStatusPill>
				</p>
				<p v-if="sourceUpdateRemovedTools.length" data-testid="source-update-removed-tools">
					<span class="text-muted-foreground">Removes tools:</span>
					<ZhStatusPill v-for="tool in sourceUpdateRemovedTools" :key="tool" tone="failed" :pulse="false" class="ml-1"><span class="font-mono line-through">{{ shownTool(tool) }}</span></ZhStatusPill>
				</p>
				<p v-if="sourceUpdateRemovedGates.length" data-testid="source-update-removed-gates">
					<span class="text-muted-foreground">Removes gates:</span>
					<ZhStatusPill v-for="gate in sourceUpdateRemovedGates" :key="gate" tone="failed" :pulse="false" class="ml-1"><span class="font-mono line-through">{{ gate }}</span></ZhStatusPill>
				</p>
				<p v-if="sourceUpdateMissingInputs.length" data-testid="source-update-missing-inputs">
					<span class="text-muted-foreground">Needs input values:</span>
					<ZhStatusPill v-for="input in sourceUpdateMissingInputs" :key="input.key" tone="action" :pulse="false" class="ml-1"><span class="font-mono">{{ input.label }}</span></ZhStatusPill>
					<span class="text-muted-foreground ml-1">Set them under Input values, before or after updating, so runs of the new version have them.</span>
				</p>
				<p v-if="sourceUpdateUnchanged" class="text-muted-foreground">It adds no tools or gates, removes none, and needs no input values.</p>
				<!-- Why it is waiting (#763), before anyone clicks Update: the plan case takes the refusal's own Billing link. -->
				<p v-if="sourceUpdateHeldLine" data-testid="source-update-held">
					{{ sourceUpdateHeldLine }}
					<RouterLink v-if="sourceUpdateHeld?.reasons.includes('crafted_not_in_plan')" class="underline" to="/billing#plans">See plans on Billing</RouterLink>
				</p>
				<Button type="button" size="sm" :disabled="taskBusy" @click="updateFromSource">Update to v{{ sourceUpdate.sourceVersion }}</Button>
			</ZhPanel>
			<TaskCopyForm v-if="copying && !isOs" :key="task.id" :source="task" :roles="roles" :create-copy="createCopy" :disabled="operationBusy" @copied="copied" @cancel="copying = false" @busy-change="copyBusy = $event" />

			<ZhPanel v-if="task.definitionIssues?.length" class="space-y-1 text-sm">
				<p class="flex items-center gap-2 font-medium">
					<!-- The kit's action-required pill, glyph only: the heading beside it says what needs doing. -->
					<ZhStatusPill tone="action" :pulse="false" aria-hidden="true" />
					This task will not run cleanly as defined
				</p>
				<DefinitionIssueLine v-for="issue in task.definitionIssues" :key="issue.code + issue.message" :issue="issue" @cloned="load" />
			</ZhPanel>

			<!-- Overview reads as a small flow plus facts; the JSON schema editor stays behind Edit. -->
			<template v-if="!editing">
				<!-- The task's place in the flow: predecessors (blue, left), successors (green, right). -->
				<div
					v-if="predecessorLinks.length || successorLinks.length"
					class="grid gap-3"
					:class="predecessorLinks.length && successorLinks.length ? 'sm:grid-cols-2' : ''"
				>
					<TaskFlowLane variant="predecessor" :links="predecessorLinks" />
					<TaskFlowLane variant="successor" :links="successorLinks" />
				</div>

				<section class="grid gap-3 sm:grid-cols-2">
					<p class="sm:col-span-2"><span class="text-muted-foreground text-sm">Goal</span><br />{{ task.currentVersion?.goal || '—' }}</p>
					<p class="sm:col-span-2" data-testid="task-metrics">
						<span class="text-muted-foreground text-sm">Metrics</span><br />
						<template v-if="currentMetrics.length">
							<span v-for="metric in currentMetrics" :key="metric" class="block">{{ metric }}</span>
						</template>
						<template v-else>—</template>
					</p>
					<div v-if="taskKpis?.length" class="sm:col-span-2" data-testid="task-kpis">
						<span class="text-muted-foreground text-sm">KPIs</span>
						<TaskKpiReview
							class="mt-1"
							:task-id="task.id"
							:kpis="taskKpis"
							:can-edit="canEditDefinition"
							:base-version-id="task.currentVersionId"
							@changed="kpisChanged"
						/>
					</div>
					<p v-else-if="taskKpis === null" class="text-muted-foreground text-sm sm:col-span-2" data-testid="task-kpis-error">
						Could not load this task's KPIs.
					</p>
					<p class="sm:col-span-2" data-testid="task-runs-as">Runs as: {{ runsAsLabel }}</p>
					<template v-if="!isOs">
						<p v-if="task.lastRunAssigneeName" class="sm:col-span-2" data-testid="task-last-run">Last run: {{ task.lastRunAssigneeName }}</p>
						<TaskRoleAssignment :key="task.id" :task="task" :roles="roles" :can-edit="canAssignRole" @changed="load" />
						<p data-testid="task-teams"><span class="text-muted-foreground text-sm">Teams</span><br />{{ taskTeamsLabel }}</p>
						<p data-testid="task-runs-at-once"><span class="text-muted-foreground text-sm">Runs at once</span><br />{{ task.serializeOn?.length ? `One per ${task.serializeOn.join(' + ')}` : 'One at a time' }}</p>
						<p><span class="text-muted-foreground text-sm">Status</span><br />{{ task.status }}</p>
					</template>
					<div v-if="toolChips.length" class="sm:col-span-2">
						<p class="text-muted-foreground text-sm">Tools</p>
						<div class="mt-1 flex flex-wrap gap-2">
							<ZhTooltip v-for="chip in toolChips" :key="chip.vendor" :text="chip.methods.map(shownTool).join(', ')">
								<ZhSystemLabel as="span" data-testid="tool-chip" class="inline-flex items-center gap-1.5">
									<Wrench class="size-3 shrink-0" aria-hidden="true" />
									{{ toolLabel(chip.vendor, uiMode) }}
								</ZhSystemLabel>
							</ZhTooltip>
						</div>
					</div>
					<div v-if="toolSlots.length" class="sm:col-span-2" data-testid="task-tool-slots">
						<p class="text-muted-foreground text-sm">Tool slots</p>
						<div class="mt-1 flex flex-wrap gap-2">
							<ZhTooltip v-for="slot in toolSlots" :key="slot.key" :text="slot.description || slot.label">
								<ZhSystemLabel as="span" data-testid="tool-slot-chip">
									{{ slot.label }} <span class="text-muted-foreground font-mono">{{ slot.key }}</span>
								</ZhSystemLabel>
							</ZhTooltip>
						</div>
					</div>
					<div v-if="mailboxAction" class="sm:col-span-2" data-testid="task-mailbox-action">
						<p class="text-muted-foreground text-sm">Mailbox button</p>
						<p class="mt-1 text-sm">
							<ZhSystemLabel as="span">{{ mailboxAction.label }}</ZhSystemLabel>
							<span class="text-muted-foreground"> {{ MAILBOX_SURFACE_TEXT[mailboxAction.on] }}</span>
						</p>
					</div>
					<div v-if="gateBadges.length" class="sm:col-span-2">
						<p class="text-muted-foreground text-sm">Gates</p>
						<div class="mt-1 flex flex-wrap gap-2">
							<ZhTooltip
								v-for="badge in gateBadges"
								:key="badge.key"
								:text="`${shownTool(badge.tool)} waits on ${badge.owner} (${badge.approval})`"
							>
								<!-- A gate waits on someone before its tool runs: the kit's action-required pill says so. -->
								<ZhStatusPill tone="action" :pulse="false" data-testid="gate-badge">
									<span class="font-mono">{{ shownTool(badge.tool) }}</span>
									<span class="text-muted-foreground">· {{ badge.owner }}</span>
								</ZhStatusPill>
							</ZhTooltip>
						</div>
					</div>
					<div v-if="spendChips.length" class="sm:col-span-2">
						<p class="text-muted-foreground text-sm">Spend</p>
						<div class="mt-1 flex flex-wrap gap-2">
							<ZhSystemLabel v-for="chip in spendChips" :key="chip" as="span">{{ chip }}</ZhSystemLabel>
						</div>
					</div>
				</section>
			</template>

			<!-- Every control is the kit's (#1227), on a kit surface: the form states no colour, radius, border or fill. -->
			<ZhPanel v-else as="form" class="space-y-3" @submit.prevent="save">
				<!--
					#1757: a save refused because the task moved on. The edits stay in the form; the panel names the newer
					version and what it changed, and offers Merge or Reload. Neither publishes: Save does, against it.
				-->
				<ZhPanel
					v-if="conflict"
					:id="CONFLICT_PANEL_ID"
					as="div"
					class="space-y-3"
					role="region"
					aria-labelledby="task-conflict-heading"
					tabindex="-1"
					data-testid="task-conflict-panel"
				>
					<ZhStatusPill id="task-conflict-heading" tone="action" :pulse="false">Changed while you were editing</ZhStatusPill>
					<p class="text-sm">
						This task has a newer version than the one you were editing<template v-if="conflictVersion?.number != null">: version
						{{ conflictVersion.number }}</template><template v-if="conflictVersion?.savedAt">, saved {{ conflictVersion.savedAt }}</template><template v-if="conflictVersion?.by"> by {{ conflictVersion.by }}</template>.
						Your edits are still in the form.
					</p>
					<template v-if="conflict.confirmingReload">
						<p class="text-sm" data-testid="task-conflict-reload-question">
							Reload drops every edit you made in this form and loads the newer version. Drop your edits?
						</p>
						<div class="flex flex-wrap gap-2">
							<Button type="button" size="sm" variant="secondary" @click="reloadConflict">Drop my edits and reload</Button>
							<Button type="button" size="sm" variant="ghost" @click="keepEditsOnReload">Keep my edits</Button>
						</div>
					</template>
					<template v-else-if="!conflict.picking">
						<p class="text-sm">{{ conflict.changed.length ? 'It changed:' : 'It changed none of the fields on this form.' }}</p>
						<ul v-if="conflict.changed.length" class="list-disc space-y-1 pl-5 text-sm" data-testid="task-conflict-fields">
							<li v-for="field in conflict.changed" :key="field.key">
								{{ field.label }}<span v-if="field.both" class="text-muted-foreground"> · changed on both sides</span>
							</li>
						</ul>
						<p class="text-muted-foreground text-xs">
							Merge keeps your edits on top of the newer version. Reload takes the newer version and drops your
							edits. Neither saves anything: Save does, as the version after it.
						</p>
						<div class="flex flex-wrap gap-2">
							<Button type="button" size="sm" variant="secondary" @click="mergeConflict">Merge</Button>
							<Button type="button" size="sm" variant="ghost" @click="reloadConflict">Reload</Button>
						</div>
					</template>
					<template v-else>
						<p class="text-sm">Pick a side for each field you both changed. Each is taken whole.</p>
						<div
							v-for="field in conflict.changed.filter(row => row.both)"
							:key="field.key"
							class="space-y-2"
							data-testid="task-conflict-choice"
						>
							<p class="text-sm font-medium">{{ field.label }}</p>
							<div class="grid gap-2 sm:grid-cols-2">
								<div class="space-y-1">
									<ZhSystemLabel as="span">Yours</ZhSystemLabel>
									<p class="text-sm break-words whitespace-pre-wrap">{{ shownValue(field.mine) }}</p>
								</div>
								<div class="space-y-1">
									<ZhSystemLabel as="span">Theirs</ZhSystemLabel>
									<p class="text-sm break-words whitespace-pre-wrap">{{ shownValue(field.theirs) }}</p>
								</div>
							</div>
							<div class="flex flex-wrap gap-2">
								<Button
									type="button"
									size="sm"
									:variant="conflict.choices[field.key] === 'mine' ? 'secondary' : 'ghost'"
									@click="chooseConflictSide(field.key, 'mine')"
								>
									Keep mine
								</Button>
								<Button
									type="button"
									size="sm"
									:variant="conflict.choices[field.key] === 'theirs' ? 'secondary' : 'ghost'"
									@click="chooseConflictSide(field.key, 'theirs')"
								>
									Take theirs
								</Button>
							</div>
						</div>
					</template>
				</ZhPanel>
				<fieldset :disabled="saving || !canEditDefinition" class="space-y-3">
				<p class="text-sm">Save writes a new immutable version. In-flight runs keep the old pin.</p>
				<div class="grid gap-3 sm:grid-cols-2">
					<div class="grid content-start gap-1">
						<label class="grid gap-1 text-sm">Name<Input v-model="form.name" v-bind="fields.bind('name')" /></label>
						<ZhFieldError v-bind="fields.message('name')" />
					</div>
					<div class="grid content-start gap-1">
						<label class="grid gap-1 text-sm">Slug<Input v-model="form.slug" v-bind="fields.bind('slug')" /></label>
						<ZhFieldError v-bind="fields.message('slug')" />
					</div>
					<!-- #1749: Kind is Advanced only. Saving from Standard sends the stored kind back unchanged. -->
					<div v-if="advancedMode" class="grid gap-1 text-sm">
						<span id="task-edit-kind">Kind</span>
						<Select v-model="form.kind">
							<SelectTrigger class="w-full" aria-labelledby="task-edit-kind">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="llm">llm</SelectItem>
								<SelectItem value="deterministic">deterministic</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div class="grid gap-1 text-sm">
						<span id="task-edit-status">Status</span>
						<Select v-model="form.status">
							<SelectTrigger class="w-full" aria-labelledby="task-edit-status">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="active">active</SelectItem>
								<SelectItem value="deprecated">deprecated</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div class="grid gap-1 text-sm">
						<span id="task-edit-role">Role</span>
						<!-- Named, so the form carries Reka's own native select and the browser still refuses an empty role. -->
						<Select
							:model-value="roleChoice"
							name="assigneeRole"
							required
							data-testid="task-assignee-role-select"
							@update:model-value="chooseRole"
						>
							<SelectTrigger class="w-full" aria-labelledby="task-edit-role" v-bind="fields.bind('assigneeRole')">
								<SelectValue :placeholder="teamRoles.length > 0 ? 'Choose a role' : 'Create a role first'" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="role in teamRoles" :key="role.slug" :value="role.slug">{{ role.name }}</SelectItem>
							</SelectContent>
						</Select>
						<ZhFieldError v-bind="fields.message('assigneeRole')" />
						<span class="text-muted-foreground text-xs">Each run goes to a holder of this role, whatever team they sit on.</span>
					</div>
					<!-- Text, not a number field: a browser empties a number field it cannot parse, so nothing could say why. -->
					<div class="grid content-start gap-1">
						<label class="grid gap-1 text-sm">Spend USD / run<Input v-model="form.spendUsd" inputmode="decimal" v-bind="fields.bind('spendUsd')" /></label>
						<ZhFieldError v-bind="fields.message('spendUsd')" />
					</div>
					<ModelTierField :form="form" />
				</div>
				<label class="grid gap-1 text-sm">Goal<Textarea v-model="form.goal" rows="2" /></label>
				<label class="grid gap-1 text-sm">Metrics, one per line<Textarea v-model="form.metric" rows="3" /></label>
				<label class="grid gap-1 text-sm">Guardrails (one per line)<Textarea v-model="form.guardrails" rows="4" /></label>
				<div class="relative text-sm">
					<TaskToolsPicker @require-gates="requireToolGates" v-model="form.tools" v-model:optional="form.optionalTools" :team-ids="taskTeamIds" :role-id="teamRoles.find(role => role.slug === form.assigneeRole)?.id" :task-id="task?.id" />
					<DocsLink path="/docs/tools/built-in" class="absolute top-0 right-0" />
				</div>
				<TaskToolSlotsEditor v-model="toolSlotRows" />
				<TaskInputsEditor v-model="inputRows" data-testid="task-inputs-editor" />
				<TaskGatesEditor v-model="gateRows" data-testid="task-gates-editor" />
				<div class="grid gap-1">
					<label class="grid gap-1 text-sm">Workspace JSON<Textarea v-model="form.workspace" rows="4" v-bind="fields.bind('workspace')" /></label>
					<ZhFieldError v-bind="fields.message('workspace')" />
				</div>
				<TaskSuccessorsEditor
					v-model="successorRows"
					:tasks="successorTasks"
					:outcomes="successorOutcomes"
					data-testid="task-successors-editor"
				/>
				<label class="grid gap-1 text-sm">
					Finishes the work on (one outcome per line)
					<Textarea v-model="form.finishesOn" rows="2" data-testid="task-finishes-on" />
					<span class="text-muted-foreground text-xs">
						A run that ends on one of these, with no step after it, finishes the work, so it is not listed as waiting.
						Use succeeded for a plain success. Left empty, the work stays open until someone marks it finished.
					</span>
				</label>
				<div class="grid gap-3 sm:grid-cols-2" data-testid="task-mailbox-action-fields">
					<div class="grid gap-1 text-sm">
						<span id="task-mailbox-action-on-label">Mailbox button</span>
						<Select
							:model-value="form.mailboxActionOn || 'none'"
							aria-label="Mailbox button"
							@update:model-value="value => { form.mailboxActionOn = value === 'mailbox' || value === 'mail' ? value : '' }"
						>
							<SelectTrigger aria-labelledby="task-mailbox-action-on-label" data-testid="task-mailbox-action-on">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="none">None</SelectItem>
								<SelectItem value="mailbox">On the mailbox (above its list)</SelectItem>
								<SelectItem value="mail">On one open mail (passes its id as emailId)</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<label class="grid gap-1 text-sm">
						Button label
						<Input v-model="form.mailboxActionLabel" maxlength="40" placeholder="Clean inbox" data-testid="task-mailbox-action-label" />
						<span class="text-muted-foreground text-xs">
							An inbox offers this task as a one-click button that starts it. A button on one mail needs an emailId input.
						</span>
					</label>
				</div>
				<label class="grid gap-1 text-sm">SKILL.md<Textarea v-model="form.skillMd" rows="10" /></label>
				<Button type="submit" size="sm" :disabled="saving">Save as new version</Button>
				</fieldset>
			</ZhPanel>

			<TaskTriggers
				v-if="!editing && task"
				:task="task"
				:input-fields="inputFields"
				:predecessors="predecessorLinks"
				:editable="!isOs"
				@changed="load"
			/>

			<TaskInputValues
				v-if="!editing && task && inputFields.length"
				:task="task"
				:input-fields="inputFields"
				:editable="!isOs"
				@changed="load"
			/>

			<section v-if="!editing && !isOs" class="space-y-2">
				<ZhSectionHeading>Recommendations</ZhSectionHeading>
				<p v-if="!recommendations.length" class="text-muted-foreground text-sm">No pending recommendations.</p>
				<DataTable
					v-else
					storage-key="task-recommendations"
					:columns="recommendationColumns"
					:rows="recommendations as unknown as Record<string, unknown>[]"
					:total="recommendations.length"
					:page="1"
					:limit="recommendations.length"
					:pages="1"
					@row-click="row => router.push(`/tasks/${task?.id}/recommendations/${String(row.id)}`)"
				>
					<template #cell-confidence="{ value }">
						{{ Math.round(Number(value) * 100) }}%
					</template>
				</DataTable>
			</section>

			<section v-if="!editing" class="space-y-2">
				<ZhSectionHeading>Start a run</ZhSectionHeading>
				<form class="flex flex-wrap items-end gap-2" @submit.prevent="start">
					<label v-for="field in inputFields" :key="field.key" class="text-sm">
						<span class="text-muted-foreground">{{ field.label }}</span>
						<input
							v-if="field.type === 'boolean'"
							v-model="runInputs[field.key]"
							class="mt-1 block"
							type="checkbox"
						/>
						<input
							v-else-if="field.type === 'file'"
							:data-testid="`start-file-${field.key}`"
							class="mt-1 block text-sm"
							type="file"
							:required="field.required"
							@change="pickFile(field.key, $event)"
						/>
						<Input
							v-else
							:model-value="startText(field.key)"
							class="mt-1"
							:required="field.required"
							:type="typedInputType(field)"
							@update:model-value="runInputs[field.key] = $event"
						/>
					</label>
					<Button type="submit" size="sm" :disabled="taskBusy || deprecated">Start</Button>
				</form>
				<p v-if="deprecated" data-testid="start-deprecated" class="text-muted-foreground text-sm">This task is deprecated, so it starts no new runs. Set its status back to active to start it.</p>
			</section>

			<section v-if="!editing && versions.length" class="space-y-2">
				<ZhSectionHeading>Version history</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">
					Runs stay on the version they started with. Pick a version to see its definition and its runs.
				</p>
				<!-- A kit surface of kit buttons: the version in view takes the kit's secondary treatment, the rest ghost. -->
				<ZhPanel as="ul" flush class="divide-y text-sm">
					<li v-for="row in versions" :key="row.id">
						<Button
							type="button"
							:variant="row.id === selectedVersionId ? 'secondary' : 'ghost'"
							class="h-auto w-full flex-wrap justify-start gap-x-3 gap-y-1 py-2 text-left whitespace-normal"
							@click="selectVersion(row.id)"
						>
							<span class="font-medium">v{{ row.version }}</span>
							<ZhSystemLabel v-if="row.id === task.currentVersionId" as="span" tone="system">current</ZhSystemLabel>
							<span class="text-muted-foreground">{{ new Date(row.createdAt).toLocaleString() }}</span>
							<!-- A version a linked clone took from its source by itself (#760) says so. -->
							<span v-if="versionOriginLabel(row)" data-testid="version-origin" class="text-muted-foreground">{{ versionOriginLabel(row) }}</span>
							<span class="text-muted-foreground ml-auto tabular-nums">{{ row.runCount ?? 0 }} runs</span>
						</Button>
					</li>
				</ZhPanel>
				<ZhPanel v-if="viewingOlderVersion && versionDetail" as="article" class="space-y-3 text-sm">
					<p class="font-medium">
						v{{ versionDetail.version }} as it was
						<span class="text-muted-foreground font-normal">(read-only; not the current definition)</span>
					</p>
					<p><span class="text-muted-foreground">Goal</span><br />{{ versionDetail.goal }}</p>
					<p v-if="versionMetrics.length">
						<span class="text-muted-foreground">Metrics</span><br />
						<span v-for="metric in versionMetrics" :key="metric" class="block">{{ metric }}</span>
					</p>
					<div>
						<p class="text-muted-foreground">Tools</p>
						<p data-testid="task-version-tools">{{ toolEntryNames(versionDetail.tools ?? []).map(shownTool).join(', ') || '—' }}</p>
					</div>
					<div>
						<p class="text-muted-foreground">Tool slots</p>
						<p data-testid="task-version-tool-slots">
							{{ (versionDetail.toolSlots ?? []).map(slot => `${slot.label} (${slot.key})`).join(', ') || '—' }}
						</p>
					</div>
					<div>
						<p class="text-muted-foreground">Successors</p>
						<ul v-if="(versionDetail.successors ?? []).length" class="list-disc pl-5">
							<li v-for="(successor, index) in versionDetail.successors ?? []" :key="`${successor.slug}-${index}`">
								{{ successor.slug }} <span class="text-muted-foreground">on {{ successor.on ?? 'succeeded' }}</span>
							</li>
						</ul>
						<p v-else>—</p>
					</div>
					<div>
						<p class="text-muted-foreground">Finishes the work on</p>
						<p data-testid="task-version-finishes-on">{{ (versionDetail.finishesOn ?? []).join(', ') || '—' }}</p>
					</div>
					<div>
						<p class="text-muted-foreground">Mailbox button</p>
						<p data-testid="task-version-mailbox-action">
							{{ versionDetail.mailboxAction ? `${versionDetail.mailboxAction.label} (${MAILBOX_SURFACE_TEXT[versionDetail.mailboxAction.on]})` : '—' }}
						</p>
					</div>
					<details>
						<summary class="text-muted-foreground cursor-pointer">Workflow (SKILL.md)</summary>
						<ZhPanel as="pre" class="mt-2 overflow-x-auto text-xs whitespace-pre-wrap">{{ versionDetail.skillMd }}</ZhPanel>
					</details>
				</ZhPanel>
			</section>

			<section v-if="!editing" class="space-y-2">
				<div class="flex flex-wrap items-baseline justify-between gap-2">
					<ZhSectionHeading>Run history
						<span v-if="selectedVersion" class="text-muted-foreground text-sm font-normal">
							· v{{ selectedVersion.version }}{{ selectedVersion.id === task.currentVersionId ? ' (current)' : '' }}
						</span></ZhSectionHeading>
					<div v-if="versions.length > 1" class="text-muted-foreground flex items-center gap-1 text-sm">
						<span id="task-run-history-version">Version</span>
						<Select :model-value="selectedVersionId ?? undefined" @update:model-value="chooseVersion">
							<SelectTrigger size="sm" aria-labelledby="task-run-history-version">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="row in versions" :key="row.id" :value="row.id">
									v{{ row.version }}{{ row.id === task.currentVersionId ? ' (current)' : '' }} · {{ row.runCount ?? 0 }} runs
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>
				<p v-if="!runTotal" class="text-muted-foreground text-sm">
					No runs on this version yet.
					<template v-if="versions.length > 1">Older runs are under earlier versions above.</template>
				</p>
				<DataTable
					storage-key="task-runs"
					:columns="runColumns"
					:rows="runs as unknown as Record<string, unknown>[]"
					:total="runTotal"
					:page="runPage"
					:limit="10"
					:pages="runPages"
					:sort="'createdAt'"
					:order="'DESC'"
					@update:page="loadRuns"
					@row-click="row => router.push(`/runs/${String(row.id)}`)"
				>
					<template #cell-id="{ row }">
						<span class="font-mono text-xs">{{ String(row.id).slice(0, 8) }}</span>
					</template>
					<template #cell-status="{ row }">
						<!-- A run that dead-ended is stored `succeeded` but stopped its pipeline: it reads stopped, and says why. -->
						<ZhTooltip :text="runStoppedLine(row as unknown as RunRow)">
							<span data-testid="task-run-row-status" :data-stopped-reason="runStoppedLine(row as unknown as RunRow) ?? undefined">{{
								runPillStatus(row as unknown as RunRow)
							}}</span>
						</ZhTooltip>
						<Button
							v-if="retryable.has(String(row.status))"
							type="button"
							size="sm"
							variant="outline"
							class="ml-2"
							:disabled="taskBusy"
							@click.stop="retryRun(String(row.id))"
						>
							Retry
						</Button>
					</template>
				</DataTable>
			</section>
		</template>
	</section>
</template>
